/** Section — BOOKED GAMES: a calendar of the real games ahead, and the ones
 *  this promotion has booked.
 *
 *  Nothing is booked by default. A game is booked because somebody clicked it,
 *  ticked it in a team run, or confirmed a weekday run. Every booking belongs
 *  to one promotion and says which one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Loader2, Settings2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { btn } from './buttons';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";

/** Every colour the calendar uses, in ONE place, so a venue's palette can be
 *  changed here and nowhere else. Home and away are read from the game itself:
 *  in CAR@MTL the team after the @ is at home. */
const CAL = {
    home: 'bg-emerald-100 text-emerald-900 border-emerald-500 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-50 dark:border-emerald-400 dark:hover:bg-emerald-900/70',
    away: 'bg-rose-100 text-rose-900 border-rose-500 hover:bg-rose-200 dark:bg-rose-900/40 dark:text-rose-50 dark:border-rose-400 dark:hover:bg-rose-900/70',
    neutral: 'bg-slate-100 text-slate-700 border-transparent hover:bg-sky-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-sky-900/40',
    past: 'bg-slate-100 text-slate-400 border-transparent dark:bg-slate-800 dark:text-slate-500',
    picked: 'bg-sky-600 text-white border-sky-700 dark:border-sky-300',
    // A booked game keeps its home/away colour and takes a hard ring, so it can
    // never be mistaken for an unbooked one in either theme.
    bookedRing: 'ring-2 ring-slate-900 dark:ring-white',
    swatchHome: 'bg-emerald-500 border-emerald-600',
    swatchAway: 'bg-rose-500 border-rose-600',
    today: 'ring-2 ring-sky-500 dark:ring-sky-400',
};

/** Home for the team in the filter, away for it, or neither. */
const sideFor = (game, team) => {
    if (!team) return '';
    if (game.home_abbrev === team) return 'home';
    if (game.away_abbrev === team) return 'away';
    return '';
};

// Monday first, which is how a calendar reads here, and the same order the
// server uses for the weekday run (0 = Monday).
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const monthId = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const addMonths = (id, n) => {
    const [y, m] = id.split('-').map(Number);
    return monthId(new Date(y, m - 1 + n, 1));
};
const monthName = (id) => {
    const [y, m] = id.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};
const clock = (iso) => (iso
    ? new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '');
const dayText = (date) => {
    if (!date) return '';
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined,
        { weekday: 'short', day: 'numeric', month: 'short' });
};
const isPast = (iso) => !!iso && new Date(iso).getTime() < Date.now();

/** One booked game's own settings. Saved for that game only — the promotion's
 *  defaults and the other bookings are untouched. */
const BookingSettings = ({ booking, auto, orgId, onSaved, onClose }) => {
    const base = auto?.settings || {};
    const o = booking.overrides || {};
    const [draft, setDraft] = useState({
        team_side: o.team_side || base.team_side || 'either',
        max_rounds: o.max_rounds ?? base.max_rounds,
        gap_seconds: o.gap_seconds ?? base.gap_seconds,
        triggers: { ...(base.triggers || {}), ...(o.triggers || {}) },
    });
    const [busy, setBusy] = useState(false);

    const keys = auto?.trigger_keys || [];
    const label = (key) => {
        if (key === 'goal') return auto?.score_word || 'Goal';
        if (key === 'game_start') return 'Game start';
        if (key === 'game_end') return 'Game end';
        return `End of ${(auto?.segment_word || 'period').toLowerCase()} ${key.replace('period_end_', '')}`;
    };

    const send = async (body, okText) => {
        setBusy(true);
        try {
            const res = await api.patch(`/goal-game/bookings/${booking.id}`,
                { organization_id: orgId, ...body });
            onSaved(res.data.bookings || []);
            toast.success(okText);
            onClose();
        } catch (_e) { toast.error('Could not save this game'); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-900/10 p-3 space-y-3"
             data-testid={`gg-booking-settings-${booking.id}`}>
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {booking.label} · {dayText(booking.game_date)} — this game only
                </p>
                <button type="button" onClick={onClose} data-testid={`gg-booking-settings-close-${booking.id}`}
                        className="text-slate-500 hover:text-slate-900 dark:hover:text-white">
                    <X size={15} />
                </button>
            </div>

            <div className="space-y-1">
                <p className="text-xs text-slate-500">Whose {(auto?.score_word || 'goal').toLowerCase()}s open a round</p>
                <div className="flex flex-wrap gap-2">
                    {[['home', `Home: ${booking.home_abbrev}`], ['away', `Away: ${booking.away_abbrev}`],
                      ['either', 'Either team']].map(([value, text]) => (
                        <button key={value} type="button"
                                onClick={() => setDraft((p) => ({ ...p, team_side: value }))}
                                data-testid={`gg-booking-side-${value}-${booking.id}`}
                                className={draft.team_side === value ? btn.smallPrimary : btn.smallSecondary}>
                            {text}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-1">
                <p className="text-xs text-slate-500">Moments that open a round</p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                    {keys.map((key) => (
                        <label key={key} className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!draft.triggers?.[key]}
                                   onChange={(e) => setDraft((p) => ({
                                       ...p, triggers: { ...p.triggers, [key]: e.target.checked } }))}
                                   data-testid={`gg-booking-moment-${key}-${booking.id}`}
                                   className="h-4 w-4 rounded border-slate-300" />
                            {label(key)}
                        </label>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <label className="block max-w-[150px]">
                    <span className="block text-xs text-slate-500 mb-1">Max rounds</span>
                    <input type="number" min={auto?.max_rounds_min} max={auto?.max_rounds_max}
                           value={draft.max_rounds}
                           onChange={(e) => setDraft((p) => ({ ...p, max_rounds: e.target.value }))}
                           data-testid={`gg-booking-max-rounds-${booking.id}`}
                           className={`${field} tabular-nums`} />
                </label>
                <label className="block max-w-[150px]">
                    <span className="block text-xs text-slate-500 mb-1">Minimum gap (s)</span>
                    <input type="number" min={auto?.gap_min} max={auto?.gap_max}
                           value={draft.gap_seconds}
                           onChange={(e) => setDraft((p) => ({ ...p, gap_seconds: e.target.value }))}
                           data-testid={`gg-booking-gap-${booking.id}`}
                           className={`${field} tabular-nums`} />
                </label>
                <button type="button" disabled={busy} className={btn.primary}
                        data-testid={`gg-booking-settings-save-${booking.id}`}
                        onClick={() => send({
                            team_side: draft.team_side, triggers: draft.triggers,
                            max_rounds: Number(draft.max_rounds), gap_seconds: Number(draft.gap_seconds),
                        }, 'Saved for this game')}>
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                    Save for this game
                </button>
                {booking.customized && (
                    <button type="button" disabled={busy} className={btn.warning}
                            data-testid={`gg-booking-settings-clear-${booking.id}`}
                            onClick={() => send({ clear: true }, 'Back to the promotion settings')}>
                        Back to promotion defaults
                    </button>
                )}
            </div>
        </div>
    );
};

/** `onFocusGame` lets the block above follow what is highlighted here: it is
 *  called with { kind, game } when a calendar game is ticked or a booking's
 *  own Settings are opened, and with null when that goes away. It is a LABEL
 *  channel only — nothing saved, read or booked changes because of it. */
export const GoalBookedGamesBlock = ({ orgId, promotionId, auto, onFocusGame,
                                      selectedBookingId = null, pushedBookings = null }) => {
    const [month, setMonth] = useState(() => monthId(new Date()));
    const [cal, setCal] = useState(null);
    const [team, setTeam] = useState('');
    const [picked, setPicked] = useState({});
    const [bookings, setBookings] = useState([]);
    const [busy, setBusy] = useState(false);
    const [weekday, setWeekday] = useState(5);
    const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
    const [to, setTo] = useState(() => new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10));
    const [preview, setPreview] = useState(null);
    const [dropped, setDropped] = useState({});
    const [editing, setEditing] = useState(null);

    // A per-game save made from the panel above hands the fresh rows back.
    useEffect(() => {
        if (pushedBookings) setBookings(pushedBookings);
    }, [pushedBookings]);

    const loadCalendar = useCallback(async (which, pick) => {
        const res = await api.get('/goal-game/bookings/calendar', {
            params: { organization_id: orgId, promotion_id: promotionId || undefined,
                      month: which, team: pick || undefined } });
        setCal(res.data);
    }, [orgId, promotionId]);

    const loadBookings = useCallback(async () => {
        const res = await api.get('/goal-game/bookings', {
            params: { organization_id: orgId } });
        setBookings(res.data.bookings || []);
    }, [orgId]);

    useEffect(() => {
        (async () => {
            try { await Promise.all([loadCalendar(month, team), loadBookings()]); }
            catch (_e) { toast.error('Could not load the schedule'); }
        })();
    }, [loadCalendar, loadBookings, month, team]);

    const bookedHere = useMemo(() => {
        const mine = bookings.filter((b) => (b.promotion_id || null) === (promotionId || null));
        return new Map(mine.map((b) => [b.game_id, b]));
    }, [bookings, promotionId]);

    const pickedCount = Object.keys(picked).length;

    const bookGames = async (games, okText) => {
        if (!games.length) return;
        setBusy(true);
        try {
            const res = await api.post('/goal-game/bookings', {
                organization_id: orgId, promotion_id: promotionId || null,
                games: games.map((g) => ({ game_id: g.id, date: g.date })) });
            setBookings(res.data.bookings || []);
            setPicked({});
            // The panel above must not keep showing the LAST game that was
            // ticked in the calendar once the booking is done.
            onFocusGame?.(null);
            await loadCalendar(month, team);
            const missing = (res.data.not_in_feed || []).length;
            toast.success(`${okText}${missing ? ` — ${missing} not in the feed` : ''}`);
        } catch (_e) { toast.error('Could not book that'); }
        finally { setBusy(false); }
    };

    const removeBooking = async (id) => {
        setBusy(true);
        try {
            const res = await api.post(`/goal-game/bookings/${id}/remove`, { organization_id: orgId });
            setBookings(res.data.bookings || []);
            await loadCalendar(month, team);
            toast.success('Booking removed');
        } catch (_e) { toast.error('Could not remove that booking'); }
        finally { setBusy(false); }
    };

    const runWeekday = async () => {
        setBusy(true);
        try {
            const res = await api.get('/goal-game/bookings/weekday-preview', {
                params: { organization_id: orgId, promotion_id: promotionId || undefined,
                          weekday, date_from: from, date_to: to, team: team || undefined } });
            setPreview(res.data);
            setDropped({});
        } catch (_e) { toast.error('Could not build that list'); }
        finally { setBusy(false); }
    };

    // The month grid, Monday first, with every real game on its day.
    const grid = useMemo(() => {
        const [y, m] = month.split('-').map(Number);
        const first = new Date(y, m - 1, 1);
        const lead = (first.getDay() + 6) % 7;                 // Monday first
        const days = new Date(y, m, 0).getDate();
        const byDay = {};
        for (const g of cal?.games || []) {
            const d = Number(g.date.slice(8, 10));
            (byDay[d] = byDay[d] || []).push(g);
        }
        const now = new Date();
        const thisMonth = monthId(now) === month;
        const cells = [];
        for (let i = 0; i < lead; i += 1) cells.push(null);
        for (let d = 1; d <= days; d += 1) {
            cells.push({ day: d, games: byDay[d] || [],
                         today: thisMonth && d === now.getDate() });
        }
        return cells;
    }, [month, cal]);

    return (
        <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
             data-testid="gg-booked-games">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 grow">
                    <CalendarDays size={13} className="inline mr-1.5 -mt-0.5" />
                    Booked games — {(cal?.league || auto?.league || '').toUpperCase()} calendar
                </p>
                <button type="button" onClick={() => setMonth(addMonths(month, -1))}
                        data-testid="gg-cal-prev" className={btn.smallSecondary}>
                    <ChevronLeft size={14} />
                </button>
                <span className="text-sm font-semibold min-w-[130px] text-center" data-testid="gg-cal-month">
                    {monthName(month)}
                </span>
                <button type="button" onClick={() => setMonth(addMonths(month, 1))}
                        data-testid="gg-cal-next" className={btn.smallSecondary}>
                    <ChevronRight size={14} />
                </button>
                <select value={team} onChange={(e) => { setTeam(e.target.value); setPicked({}); }}
                        data-testid="gg-cal-team" className={`${field} max-w-[170px]`}>
                    <option value="">Every team</option>
                    {(cal?.teams || []).map((ab) => <option key={ab} value={ab}>{ab}</option>)}
                </select>
            </div>

            {cal && cal.available === false && (
                <p className="text-xs text-amber-600" data-testid="gg-cal-unavailable">
                    The feed has no schedule for this league right now.
                </p>
            )}
            {cal && cal.available !== false && (cal.games || []).length === 0 && (
                <p className="text-xs text-amber-600" data-testid="gg-cal-empty">
                    No games this month{team ? ` for ${team}` : ''}.
                </p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500"
                 data-testid="gg-cal-legend">
                {team ? (
                    <>
                        <span className="inline-flex items-center gap-1.5">
                            <span className={`h-3 w-3 rounded-sm border ${CAL.swatchHome}`} />
                            <span className="font-semibold">H</span> Home
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className={`h-3 w-3 rounded-sm border ${CAL.swatchAway}`} />
                            <span className="font-semibold">A</span> Away
                        </span>
                        <span className="opacity-80">Ringed = booked · • = own settings</span>
                    </>
                ) : (
                    <span data-testid="gg-cal-legend-neutral">
                        Pick a team to see home and away.
                    </span>
                )}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center" data-testid="gg-cal-grid">
                {SHORT.map((d) => (
                    <p key={d} className="text-[10px] uppercase tracking-wider text-slate-400 pb-1">{d}</p>
                ))}
                {grid.map((cell, i) => (
                    <div key={i} data-testid={cell ? `gg-cal-day-${month}-${cell.day}` : undefined}
                         className={`min-h-[62px] min-w-0 overflow-hidden rounded-lg border p-1 text-left ${cell
                             ? 'border-slate-200 dark:border-slate-700'
                             : 'border-transparent'}`}>
                        {cell && (
                            <>
                                <p className={`inline-block rounded px-1 text-[10px] tabular-nums text-slate-400 ${
                                    cell.today ? CAL.today : ''}`}
                                   data-testid={cell.today ? 'gg-cal-today' : undefined}>
                                    {cell.day}
                                </p>
                                <div className="space-y-1 mt-0.5">
                                    {cell.games.map((g) => {
                                        const booking = bookedHere.get(g.id);
                                        const already = !!booking;
                                        const on = !!picked[g.id];
                                        const past = isPast(g.start_time_utc);
                                        const side = sideFor(g, team);
                                        const tone = on ? CAL.picked
                                            : past ? CAL.past
                                                : side === 'home' ? CAL.home
                                                    : side === 'away' ? CAL.away
                                                        : CAL.neutral;
                                        return (
                                            <button key={g.id} type="button" disabled={already || past || busy}
                                                    data-testid={`gg-cal-game-${g.id}`}
                                                    data-side={side || 'none'}
                                                    data-booked={already ? 'yes' : 'no'}
                                                    data-past={past ? 'yes' : 'no'}
                                                    title={side === 'home' ? 'Home game' : side === 'away' ? 'Away game' : ''}
                                                    onClick={() => setPicked((p) => {
                                                        const next = { ...p };
                                                        if (next[g.id]) delete next[g.id]; else next[g.id] = g;
                                                        const last = Object.values(next).slice(-1)[0] || null;
                                                        onFocusGame?.(last ? { kind: 'calendar', game: last } : null);
                                                        return next;
                                                    })}
                                                    className={`relative w-full min-w-0 rounded border px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold leading-tight ${tone} ${already ? CAL.bookedRing : ''}`}>
                                                {/* The H or A says it too, so the colour is never the only clue.
                                                    It sits in the corner so it costs the box no width. */}
                                                {side && (
                                                    <span className="absolute top-0 right-0.5 font-bold opacity-80"
                                                          data-testid={`gg-cal-side-${g.id}`}>
                                                        {side === 'home' ? 'H' : 'A'}
                                                    </span>
                                                )}
                                                <span className="block truncate">{g.away_abbrev}@{g.home_abbrev}</span>
                                                <span className="block font-normal opacity-70">
                                                    {already
                                                        ? `booked${booking.customized ? ' •' : ''}`
                                                        : clock(g.start_time_utc)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button type="button" disabled={busy || !pickedCount} className={btn.primary}
                        data-testid="gg-cal-book-selected"
                        onClick={() => bookGames(Object.values(picked),
                            `${pickedCount} game${pickedCount === 1 ? '' : 's'} booked`)}>
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                    Book {pickedCount || ''} selected game{pickedCount === 1 ? '' : 's'}
                </button>
                {!!pickedCount && (
                    <button type="button" className={btn.smallSecondary}
                            onClick={() => { setPicked({}); onFocusGame?.(null); }}
                            data-testid="gg-cal-clear-selection">Clear selection</button>
                )}
                <span className="text-xs text-slate-500">
                    Tick several games — one team or any team — then confirm in one go.
                </span>
            </div>

            {/* Every <weekday> run: the list is shown first and confirmed after. */}
            <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Repeat by weekday
                </p>
                <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
                    <label className="block">
                        <span className="block text-xs text-slate-500 mb-1">Every</span>
                        <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}
                                data-testid="gg-weekday-pick" className={field}>
                            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-xs text-slate-500 mb-1">From</span>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                               data-testid="gg-weekday-from" className={field} />
                    </label>
                    <label className="block">
                        <span className="block text-xs text-slate-500 mb-1">To</span>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                               data-testid="gg-weekday-to" className={field} />
                    </label>
                    <button type="button" disabled={busy} className={`${btn.secondary} shrink-0`}
                            data-testid="gg-weekday-preview" onClick={runWeekday}>
                        Show the list
                    </button>
                    <span className="text-xs text-slate-500">
                        {team ? `${team} only` : 'the whole league'} — change the team above.
                    </span>
                </div>

                {preview && (
                    <div className="space-y-2" data-testid="gg-weekday-list">
                        {preview.games.length === 0 ? (
                            <p className="text-xs text-amber-600" data-testid="gg-weekday-empty">
                                No games on that weekday in that time frame.
                            </p>
                        ) : (
                            <>
                                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                                    {preview.games.map((g) => (
                                        <label key={g.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm"
                                               data-testid={`gg-weekday-row-${g.id}`}>
                                            <input type="checkbox" disabled={g.booked}
                                                   checked={!g.booked && !dropped[g.id]}
                                                   onChange={(e) => setDropped((p) => ({ ...p, [g.id]: !e.target.checked }))}
                                                   data-testid={`gg-weekday-tick-${g.id}`}
                                                   className="h-4 w-4 rounded border-slate-300" />
                                            <span className="tabular-nums text-xs text-slate-500 w-32 shrink-0">
                                                {dayText(g.date)} · {clock(g.start_time_utc)}
                                            </span>
                                            <span className="font-semibold">{g.label}</span>
                                            {g.booked && <span className="text-xs text-emerald-600 font-semibold">already booked</span>}
                                        </label>
                                    ))}
                                </div>
                                <button type="button" disabled={busy} className={btn.primary}
                                        data-testid="gg-weekday-confirm"
                                        onClick={() => {
                                            const take = preview.games.filter((g) => !g.booked && !dropped[g.id]);
                                            bookGames(take, `${take.length} game${take.length === 1 ? '' : 's'} booked`);
                                        }}>
                                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                                    Confirm these games
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* The booked list, every promotion of this organization. */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Booked games ({bookings.length})
                </p>
                {bookings.length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="gg-bookings-empty">
                        Nothing booked yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                        <table className="w-full text-sm" data-testid="gg-bookings-table">
                            <tbody>
                                {bookings.map((b) => (
                                    <tr key={b.id}
                                        onClick={() => onFocusGame?.(
                                            selectedBookingId === b.id ? null : { kind: 'booking', game: b })}
                                        className={`border-b border-slate-100 dark:border-slate-800 last:border-0 cursor-pointer ${
                                            selectedBookingId === b.id
                                                ? 'bg-blue-50 dark:bg-blue-900/25 ring-1 ring-inset ring-blue-400/60'
                                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                                        data-testid={`gg-booking-row-${b.id}`}>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500 tabular-nums">
                                            {dayText(b.game_date)} · {clock(b.start_time_utc)}
                                        </td>
                                        <td className="px-3 py-2 font-semibold whitespace-nowrap">{b.label}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold"
                                                  data-testid={`gg-booking-promo-${b.id}`}>
                                                {b.promotion_name}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs">
                                            {b.customized && (
                                                <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 font-semibold"
                                                      data-testid={`gg-booking-customized-${b.id}`}>
                                                    customized
                                                </span>
                                            )}
                                            {b.status !== 'booked' && (
                                                <span className="ml-1 text-slate-500" data-testid={`gg-booking-status-${b.id}`}>
                                                    {b.status}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                            <button type="button" className={btn.smallSecondary}
                                                    data-testid={`gg-booking-settings-open-${b.id}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const next = editing === b.id ? null : b.id;
                                                        setEditing(next);
                                                        onFocusGame?.(next ? { kind: 'booking', game: b } : null);
                                                    }}>
                                                <Settings2 size={13} /> Settings
                                            </button>
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap text-right">
                                            <button type="button" disabled={busy} className={btn.iconDanger}
                                                    data-testid={`gg-booking-remove-${b.id}`}
                                                    onClick={(e) => { e.stopPropagation(); removeBooking(b.id); }}>
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {editing && bookings.some((b) => b.id === editing) && (
                    <BookingSettings booking={bookings.find((b) => b.id === editing)}
                                     auto={auto} orgId={orgId}
                                     onSaved={setBookings}
                                     onClose={() => { setEditing(null); onFocusGame?.(null); }} />
                )}
                <p className="text-xs text-slate-500">
                    A booked game points this promotion at that game and switches the automation on by
                    itself when it starts, ignoring everything that happened before the start. Max
                    rounds, the minimum gap and "game end switches off" are the same rules as a
                    manual switch-on. When the game is over the promotion's own settings are put
                    back. Bookings survive a demo reset — they only go when you remove them.
                </p>
            </div>
        </div>
    );
};

export default GoalBookedGamesBlock;
